import {
  useWSSnackbar,
  WSElement,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useExternalBankAccountReconnect } from "../../../../shared/hooks/useExternalBankAccountReconnect";
import { useExternalBankAccountsPlaidLink } from "../../../../shared/hooks/useExternalBankAccountsPlaidLink";
import { selectorCountryName } from "../../../../shared/selectors/selectorCountryName";
import { useModalMicrodepositsVerify } from "../ModalMicrodepositsVerify";
import { Item } from "./Item";
import { useModalRemove } from "./ModalRemove";
import { MethodActionHandlers, MethodsData, MethodValue } from "./types";
import { getMethods } from "./utils";

function getCountryText(country: string) {
  if (country === "Platform") {
    return "Platform";
  }

  if (country === "US") {
    return "US based";
  }

  return `${selectorCountryName(country)} based`;
}

type Props = {
  data: MethodsData;

  initialValue?: MethodValue;
  value?: MethodValue | null;
  onChange: (value: MethodValue | null) => void;
  category?: string;
};

export const List: React.FC<Props> = props => {
  const modalMicrodepositsVerify = useModalMicrodepositsVerify();
  const history = useHistory();
  const { openSnackbar } = useWSSnackbar();

  const plaidLink = useExternalBankAccountsPlaidLink({
    onSuccess: () => {}
  });

  const openPlaidReconnect = useExternalBankAccountReconnect();

  const modalRemove = useModalRemove();

  const openModalRemove = useCallback(
    (value: MethodValue) =>
      modalRemove.open(value, {
        onClose: isRemoved => {
          if (!isRemoved) {
            return;
          }
          // Unset selected value if it was removed
          if (value.id === props.value?.id) {
            props.onChange(null);
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value?.id]
  );

  const handlers = useMemo(
    (): MethodActionHandlers => ({
      handleChange: props.onChange,
      openModalRemove,
      openMicrodepositsVerify: modalMicrodepositsVerify.open,
      openPlaid: plaidLink.open,
      openPlaidReconnect,
      historyPush: history.push,
      notifyAccountSelectionBlocked: () =>
        openSnackbar({
          type: "warning",
          message:
            'Reconnect this account with Plaid before selecting it — click "Reconnect with Plaid" on the account.'
        })
    }),
    [
      openModalRemove,
      history.push,
      modalMicrodepositsVerify.open,
      openPlaidReconnect,
      openSnackbar,
      plaidLink.open,
      props.onChange
    ]
  );

  const methodsByCountry = useMemo(
    () => getMethods(props.data, handlers, props.category),
    [handlers, props.category, props.data]
  );

  const showCountry = useMemo(() => {
    const countries = Object.keys(methodsByCountry);
    return countries.length > 1 || !countries.includes("US");
  }, [methodsByCountry]);

  return (
    <WSList gap="XL">
      {methodsByCountry.map(({ country, methods }) => (
        <WSElement key={[props.category, country].join("-")}>
          {showCountry && (
            <WSText.Caption weight="medium" mb="M">
              {getCountryText(country)}
            </WSText.Caption>
          )}

          <WSList gap="S">
            {methods.map(method => (
              <Item
                key={[props.category, country, method.id].join("-")}
                {...method}
                initialActive={
                  method.id === props.initialValue?.id &&
                  method.category === props.initialValue?.category
                }
                active={
                  method.id === props.value?.id &&
                  method.category === props.value?.category
                }
              />
            ))}
          </WSList>
        </WSElement>
      ))}
    </WSList>
  );
};
